<script setup lang="ts">
import { useField } from 'vee-validate';
import Attachments from '@/components/forms/Attachments/Attachments.vue';
import type { VeeAttachmentProps } from '../types';
import { computed, toRef, ref } from 'vue';
import { UploadProgress } from '@/utils/useFileUpload';
import { useVeeFormProps } from '../utils';
import { useUploadValidation } from '@/utils/useUploadValidation';
import { FileMetaInfoDto } from '@/api/generator';

const props = defineProps<VeeAttachmentProps>();

const veeProps = useVeeFormProps(props);

const { value, handleChange, errorMessage } = useField<FileMetaInfoDto[]>(
  toRef(() => props.name),
  undefined,
  {
    validateOnValueUpdate: false,
  },
);

const onUpdate = (input: FileMetaInfoDto[] | undefined) => {
  handleChange(input, true);
};

const currentUploadProgresses = ref<UploadProgress[]>();

const onUploadProgressUpdate = (uploadProgress: UploadProgress[]) => {
  currentUploadProgresses.value = uploadProgress;
};

const isUploadRunning = computed(() =>
  Boolean(
    currentUploadProgresses.value?.some(
      (currentUploadProgress) =>
        currentUploadProgress.state === 'pending' || currentUploadProgress.state === 'idle',
    ),
  ),
);

const isUploadError = computed(() =>
  Boolean(
    currentUploadProgresses.value?.some(
      (currentUploadProgress) => currentUploadProgress.state === 'failed',
    ),
  ),
);

useUploadValidation({
  isUploadRunning,
  isUploadError,
  name: toRef(() => props.name),
});
</script>

<template>
  <Attachments
    v-bind="veeProps"
    :modelValue="value"
    :data-testid="$attrs['data-testid']"
    :errorMessage="errorMessage"
    @update:modelValue="onUpdate"
    @update:uploadProgress="onUploadProgressUpdate"
  />
</template>
